import {
    AccountMappingTemplateGroupItem,
    AccountType,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    IAccountMappingTemplateGroupItemSubtotal,
    ItemType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { FinancialLineItemType } from '@modeso/types__aumico-lib-settings-be';
import { IAddSubtotalFormDialogResult } from '../interfaces/add-subtotal-form-dialog-result.interface';
import { IFliFormDialogResult } from '../interfaces/fli-form-dialog-result.interface';
import { Parent } from '../interfaces/menu-item.class';
import { AccountMappingEngine } from '../mappers/account-mapping-engin';
import { INameIdWithSortedItems } from '../types/name-id-with-sorted-items.type';

export class GroupAccountMappingFormHelper {
    static getAllGroupsOptions(groups: IAccountMappingTemplateGroup[]): INameId[] {
        let result: INameId[] = [];

        for (let group of groups) {
            if (group.isHeader || group.isEmpty) {
                continue;
            }

            result.unshift({
                id: group.name,
                name: group.title,
            });

            // Recurse on nested groups
            result.unshift(...GroupAccountMappingFormHelper.getAllGroupsOptions(group.groups));
        }

        return result;
    }

    static getAllGroupsOptionsWithSortedItems(groups: IAccountMappingTemplateGroup[]): INameIdWithSortedItems[] {
        let result: INameIdWithSortedItems[] = [];

        for (let group of groups) {
            if (group.isHeader || group.isEmpty) {
                continue;
            }

            result.push({
                id: group.name,
                name: group.title,
                sortedItems: group.sortedItems,
            });

            // Recurse on nested groups
            result = result.concat(GroupAccountMappingFormHelper.getAllGroupsOptionsWithSortedItems(group.groups));
        }

        return result;
    }

    static getItemParentGroupAndGrandparentsGroups(
        root: IAccountMappingTemplateGroup,
        targetItem: Parent,
        path: IAccountMappingTemplateGroup[],
    ): boolean {
        // this function is to get the parent group and all the grandparents groups
        if (root === targetItem) {
            path.push(root);
            return true;
        }
        for (const child of root.groups) {
            if (GroupAccountMappingFormHelper.getItemParentGroupAndGrandparentsGroups(child, targetItem, path)) {
                path.push(root); // Add the parent to the path
                return true;
            }
        }
        return false;
    }

    static findGroupByName(
        groupName: string,
        groups: IAccountMappingTemplateGroup[],
    ): IAccountMappingTemplateGroup | null {
        for (const group of groups) {
            // Check if the current group name matches
            if (group.name === groupName) {
                return group;
            }

            // Search in sub-groups
            const found = GroupAccountMappingFormHelper.findGroupByName(groupName, group.groups);
            if (found !== null) {
                return found;
            }
        }

        return null;
    }

    static constructFli(
        fliFormResult: IFliFormDialogResult,
        fliToUpdate?: IAccountMappingTemplateGroupItemFLI,
    ): IAccountMappingTemplateGroupItemFLI {
        return {
            name: fliToUpdate?.name ?? fliFormResult.fliName,
            caption: fliFormResult.fliCaption,
            type: ItemType.FLI,
            cashFlowType:
                fliFormResult.fliCashFlowType != null ? parseInt(fliFormResult.fliCashFlowType.id) : undefined,
            fliType: fliFormResult.fliType.id as FinancialLineItemType,
            accounts: fliToUpdate == null ? [] : fliToUpdate.accounts,
            reclassesAccounts: fliToUpdate == null ? [] : fliToUpdate.reclassesAccounts,
            calculatedOpeningBalance: fliToUpdate == null ? 0 : fliToUpdate.calculatedOpeningBalance,
            calculatedClosingBalance: fliToUpdate == null ? 0 : fliToUpdate.calculatedClosingBalance,
            closingAdjustment: fliToUpdate == null ? 0 : fliToUpdate.closingAdjustment,
            closingReClass: fliToUpdate == null ? 0 : fliToUpdate.closingReClass,
            openingAdjustment: fliToUpdate == null ? 0 : fliToUpdate.openingAdjustment,
            openingReClass: fliToUpdate == null ? 0 : fliToUpdate.openingReClass,
        };
    }

    public static constructAddSubtotal(
        subtotalFormResult: IAddSubtotalFormDialogResult,
        group: IAccountMappingTemplateGroup,
    ): IAccountMappingTemplateGroupItemSubtotal {
        return {
            name: subtotalFormResult.subtotalName,
            caption: subtotalFormResult.subtotalName,
            type: ItemType.SUB_TOTAL,
            scope: { name: group.name, type: ItemType.GROUP },
        };
    }

    static mapAccountMappingFliToFormFli(
        fli: IAccountMappingTemplateGroupItemFLI,
        parentGroup: IAccountMappingTemplateGroup,
        cashFlowTypeOptions: INameId[],
        fliTypeOptions: INameId[],
        groupOptions: INameId[],
    ): IFliFormDialogResult {
        const isUncoveredShortfallFli =
            fli.accounts.find((account) => account.type === AccountType.UNCOVERED_SHORTFALL) != null;

        return {
            fliName: fli.name,
            fliCaption: fli.caption,
            fliCashFlowType:
                fli.cashFlowType != null
                    ? cashFlowTypeOptions.find((type) => type.id === fli.cashFlowType.toString())
                    : null,
            fliType: fliTypeOptions.find((type) => type.id === fli.fliType.toString()),
            parentGroup: groupOptions.find((group) => group.id === parentGroup.name),
            isUncoveredShortfallFli,
        };
    }

    public static mapFlisNameIdToGroupItem(
        selectedFlis: INameId[],
        allFlisInTemplate: IAccountMappingTemplateGroupItemFLI[],
        engine: AccountMappingEngine,
    ): AccountMappingTemplateGroupItem[] {
        const accountMappingFlis: AccountMappingTemplateGroupItem[] = [];

        selectedFlis.forEach((selectedFli) => {
            const selectedFliObject = allFlisInTemplate.find((fli) => fli.name === selectedFli.id);

            if (selectedFliObject != null) {
                accountMappingFlis.push(selectedFliObject);
            }
        });
        return accountMappingFlis;
    }

    public static matchFlisNameIdToGroupItem(
        selectedFlis: INameId[],
        allFinancialLineItems: IAccountMappingTemplateGroupItemFLI[],
        engine: AccountMappingEngine,
    ): AccountMappingTemplateGroupItem[] {
        const accountMappingFlis: AccountMappingTemplateGroupItem[] = [];

        selectedFlis.forEach((selectedFli) => {
            const selectedFliObject = allFinancialLineItems.find((fli) => fli.name === selectedFli.id);

            // Move fli to the new group with all it's accounts and
            // remove it from the first group

            accountMappingFlis.push(selectedFliObject);
        });
        return accountMappingFlis;
    }
}
